<template>
  <el-dialog :visible="display" @open="open" @closed="closed"
             @close="$emit('close', false)" @opened="opened"
             destroy-on-close append-to-body fullscreen>
    <template #title>
      <h3 class="text-center">{{ $t(`item.title.${edit ? 'update' : 'create'}`, { name: item.name }) }}</h3>
      <hr>
    </template>
    <el-row>
      <el-col :span="14" :offset="5">
        <el-form :model="form" size="small" :rules="rules" ref="itemForm">
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item :label="$t('item.label.name')" prop="name">
                <el-input ref="inputName" v-model="form.name" autocomplete="off" name="item-name"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="$t('item.label.short_name')" prop="short_name">
                <el-input v-model="form.short_name" autocomplete="off" name="item-short-name"
                          :placeholder="$t('item.placeholder.short_name')"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item :label="$t('item.label.category')" prop="category">
                <el-select class="full-width" v-model="form.category" filterable clearable
                           :placeholder="$t('item.placeholder.category')">
                  <el-option v-for="item in categories" :key="item.id" :label="item.name"
                             :value="item.id"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item :label="$t('item.label.description')" prop="description">
                <el-input type="textarea" v-model="form.description" autocomplete="off" name="item-description"></el-input>
              </el-form-item>
            </el-col>
            <el-col class="my-0">
              <el-checkbox style="margin-bottom: 20px;" v-model="form.sellable">{{
                  $t('item.placeholder.sellable')
                }}
              </el-checkbox>
            </el-col>
            <el-col :span="12">
              <el-form-item style="" :label="$t('item.label.sold_by.label')" prop="item_type">
                <el-radio-group v-model="form.sold_by" size="mini">
                  <el-radio :disabled="!form.sellable" label="each" border>{{
                      $t('item.label.sold_by.each')
                    }}
                  </el-radio>
                  <el-radio :disabled="!form.sellable" label="measure" border>{{
                      $t('item.label.sold_by.weight')
                    }}
                  </el-radio>
                </el-radio-group>
              </el-form-item>
              <el-form-item v-show="false" :label="$t('item.label.selling_price')" label-width="100" prop="unit_price">
                <el-input v-model.number="form.unit_price" autocomplete="off" name="item-unit-price"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item style="" :label="$t('item.label.item_type')" prop="item_type">
                <el-radio-group v-model="form.item_type" size="mini">
                  <el-radio label="product" border>{{ $t('item.label.type.product') }}</el-radio>
                  <el-radio label="service" border>{{ $t('item.label.type.service') }}</el-radio>
                </el-radio-group>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item :label="$t('item.label.supplier')" prop="supplier">
                <el-select class="full-width" v-model="form.supplier" filterable clearable
                           :placeholder="$t('item.placeholder.supplier')">
                  <el-option v-for="sup in suppliers" :key="sup.id" :label="sup.name"
                             :value="sup.id"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="$t('item.label.purchase_price')" label-width="100"
                            prop="purchase_price">
                <el-input v-model.number="form.purchase_price" autocomplete="off" name="item-purchase-price"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item :label="$t('item.label.minimum_price')" label-width="100" prop="minimum_price">
                <el-input v-model.number="form.minimum_price" autocomplete="off">
                  <template slot="prepend">XAF</template>
                </el-input>
                <span class="el-form-item__hint" style="font-size: 12px; color: #909399;">
                  {{ $t('item.label.minimum_price_hint') }}
                </span>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item :label="$t('item.label.unit_type')" label-width="100" prop="unit_type">
                <el-autocomplete :fetch-suggestions="fetchTypeSuggestions" @keyup.enter.native="submitForm"
                                 v-model="form.unit_type" class="full-width"></el-autocomplete>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="$t('item.label.barcode')" prop="barcode">
                <el-input v-model="form.barcode" autocomplete="off" name="item-barcode"
                          :placeholder="$t('item.placeholder.barcode')"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item :label="$t('item.label.reorder_level')" prop="reorder_level">
                <el-input v-model="form.reorder_level" :placeholder="$t('item.placeholder.reorder_level')"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="$t('item.label.optimal_order')" prop="optimal_order">
                <el-input v-model="form.optimal_order" :placeholder="$t('item.placeholder.optimal_order')"></el-input>
              </el-form-item>
            </el-col>
          </el-row>

          <el-card class="my-3">
            <div slot="header">
              <span class="font-weight-bolder h5">{{ $t('item.label.inventory') }}</span>
            </div>
            <el-row align="center" justify="right">
              <el-col :span="12" class="mb-0">
                <label> {{ $t('item.label.track') }} </label>
              </el-col>
              <el-col :span="8"></el-col>
              <el-col :span="3" class="ml-3 mb-0">
                <el-switch v-model="form.tracked"></el-switch>
              </el-col>
            </el-row>
          </el-card>
          <!--    Start variant section     -->
          <el-card class="my-3">
            <div slot="header">
              <span class="font-weight-bolder h5">{{ $t('item.label.visual') }}</span>
            </div>
            <el-row :gutter="24">
              <el-col :span="24">
                <el-row justify="space-between">
                  <el-col :span="12">
                    <el-radio v-model="form.visual_type" border label="colour" class="mb-5">{{ $t('item.label.colour') }}</el-radio>
                  </el-col>
                  <el-col :span="12" align="center">
                    <el-radio v-model="form.visual_type" border label="image" style="float: right; margin-right: 25px;">
                      {{ $t('item.label.image') }}
                    </el-radio>
                  </el-col>
                </el-row>
                <el-row justify="space-between">
                  <el-col :span="12">
                    <el-row justify="center" align="middle">
                      <el-col class="mr-1 mb-1" :key="index" v-for="(colour, index) in predefineColours" :span="4"
                              style="height: 50px; width: 50px; align-content: center;"
                              @click.native="form.visual_type === 'colour' ? setColour(colour) : null"
                              :style="{backgroundColor: form.visual_type === 'colour' ? colour.colour : '#fcfafa'}"
                              align="center" justify-self="center">
                        <span class="el-icon-check text-white" style="font-size: 24px;"
                              v-if="colour.colour === form.colour"/>
                      </el-col>
                    </el-row>
                  </el-col>
                  <el-col :span="12">
                    <el-upload :disabled="form.visual_type !== 'image'" class="avatar-uploader" action=""
                               :show-file-list="false" :on-success="handleImageSuccess"
                               :before-upload="beforeImageUpload"
                               :on-remove="handleImageRemove"
                               accept="image/*"
                               style="float: right; margin-right: 25px;">
                      <img v-if="form.image" :src="form.image" class="custom-avatar" alt="Item image">
                      <i v-else class="el-icon-plus avatar-uploader-icon"></i>
                    </el-upload>
                  </el-col>
                </el-row>
              </el-col>
            </el-row>
          </el-card>
          <!--    End visual section     -->
          <!--    Start variant section     -->
          <el-card class="mb-3">
            <div slot="header">
              <span class="font-weight-bolder h5"> {{ $t('variant.name') }} </span>
            </div>
            <div>
              <p style="color: grey;">{{ $t('variant.message.guide') }}</p>
              <el-button v-if="edit && item.variants.length" type="danger" icon="el-icon-delete"
                         @click.native="deleteAllVariants" plain>{{ $t('button.delete') }}
              </el-button>
              <el-button type="success" @click="toggleVariantDialog(true)" class="mt-2" plain>
                {{
                  item.variants && item.variants.length ? $t(`variant.button.edit_variants`) : $t(`variant.button.add_variants`)
                }}
                <i :class="`el-icon-${item.variants && item.variants.length? 'edit':'plus'}`"></i>
              </el-button> &nbsp;
              <i v-if="form.variants.length" class="text-primary">{{ $t('item.message.variant_modified') }} &nbsp;
                <el-button type="danger" size="mini" plain @click.prevent="form.variants = []">
                  {{ $t('item.button.remove') }}
                </el-button>
              </i>
              <div v-if="item.variants && item.variants.length">
                <el-divider/>
                <data-tables :key="`variants-table-${item.variants.length}`" :data="item.variants"
                             :pagination-props="{ pageSizes: [10, 15, 30, 40, 100], pageSize: 10 }" center-align>
                  <el-table-column label="S/N" type="index"></el-table-column>
                  <el-table-column show-overflow-tooltip prop="name" :label="$t('variant.table.title.variant')">
                  </el-table-column>
                  <el-table-column prop="unit_price" :label="$t('variant.table.title.unit_price')"
                                   :show-overflow-tooltip="true">
                    <template slot-scope="scope">
                      <el-input
                        v-if="editedItemVariant.id === scope.row.id && editedItemVariant.module === 'unit_price'"
                        v-model.number="scope.row.unit_price"
                        size="small"
                        @keyup.enter.native="handleVariantFieldChange(scope.row, 'unit_price')"
                        @blur="handleVariantFieldChange(scope.row, 'unit_price')"/>
                      <div v-else @click="updateEditedItemVariant(scope.row.id, 'unit_price')">
                        <span>{{ scope.row.unit_price }}</span>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column prop="purchase_price" :label="$t('variant.table.title.purchase_price')"
                                   :show-overflow-tooltip="true">
                    <template slot-scope="scope">
                      <el-input
                        v-if="editedItemVariant.id === scope.row.id && editedItemVariant.module === 'purchase_price'"
                        v-model.number="scope.row.purchase_price"
                        size="small"
                        @keyup.enter.native="handleVariantFieldChange(scope.row, 'purchase_price')"
                        @blur="handleVariantFieldChange(scope.row, 'purchase_price')"/>
                      <div v-else @click="updateEditedItemVariant(scope.row.id, 'purchase_price')">
                        <span>{{ scope.row.purchase_price }}</span>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column show-overflow-tooltip prop="code"
                                   :label="$t('variant.table.title.code')"></el-table-column>
                  <el-table-column show-overflow-tooltip prop="barcode" :label="$t('variant.table.title.barcode')">
                    <template slot-scope="scope">
                      <el-input
                        v-model="scope.row.barcode"
                        size="small"
                        v-if="editedItemVariant.id === scope.row.id && editedItemVariant.module === 'barcode'"
                        @keyup.enter.native="handleVariantFieldChange(scope.row, 'barcode')"
                        @blur="handleVariantFieldChange(scope.row, 'barcode')"/>
                      <div v-else @click="updateEditedItemVariant(scope.row.id, 'barcode')">
                        <span>{{ scope.row.barcode }}</span>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column :label="$t('label.action')" min-width="120px" align="center">
                    <template slot-scope="scope">
                      <el-button
                        v-if="isVariantModified(scope.row.id)"
                        icon="el-icon-check"
                        @click="saveVariant(scope.row)"
                        :loading="isVariantSaving(scope.row.id)"
                        type="success"
                        size="mini"
                        circle
                        plain
                        :title="$t('button.save') || 'Save changes'">
                      </el-button>
                      <el-button
                        icon="el-icon-delete"
                        v-can.disable="'variant.delete'"
                        @click="itemVariantDelete(scope.$index, scope.row.id)"
                        :loading="loading"
                        type="danger"
                        size="mini"
                        :title="$t('button.delete') || 'Delete'">
                      </el-button>
                    </template>
                  </el-table-column>
                </data-tables>
              </div>
            </div>
          </el-card>
          <!--    End variant section     -->
          <!--    Start modifier section     -->
          <el-card v-if="modifiers.length > 0" style="margin-bottom: 10px;">
            <div slot="header">
              <span class="font-weight-bolder h5"> {{ $t('modifier.name') }} </span>
            </div>
            <div v-for="(modifier, index) in modifiers" :key="modifier.id">
              <el-row align="center" justify="right">
                <el-divider v-if="index > 0" class="my-3"/>
                <el-col :span="12" class="mb-0">
                  <label> {{ modifier.name }} </label>
                </el-col>
                <el-col :span="8"></el-col>
                <el-col :span="3" class="ml-3 mb-0">
                  <el-switch v-model="modifier.selected" @change="updateItemModifier($event, modifier)"></el-switch>
                </el-col>
              </el-row>
            </div>
          </el-card>
          <!--    End modifier section     -->
          <!--    Start tax section     -->
          <el-card v-if="taxes.length" style="margin-bottom: 10px;">
            <template #header>
              <span class="font-weight-bolder h5"> {{ $t('tax.name') }} </span>
            </template>
            <div v-for="(tax, index) in taxes" :key="tax.id">
              <el-row align="center" justify="space-between">
                <el-divider v-if="index > 0" class="my-3"/>
                <el-col :span="12" class="mb-0">
                  <label> {{ tax.name }} </label>
                </el-col>
                <el-col :span="8"></el-col>
                <el-col :span="3" class="ml-3 mb-0">
                  <el-switch v-model="tax.selected" @change="updateItemTax($event, tax)"></el-switch>
                </el-col>
              </el-row>
            </div>
          </el-card>
          <!--    End tax section     -->
          <!--    Start price list section     -->
          <el-table size="small" stripe :data="form.price_lists" style="width: 100%;"
                    header-cell-class-name="bg-secondary" class="mt-4">
            <el-table-column label-class-name="text-white text-center" :label="$t('item.label.price_list_prices')">
              <el-table-column :label="$t('price_list.label.description')"
                               prop="name" label-class-name="text-white" class-name="text-dark">
              </el-table-column>
              <el-table-column :label="$t('item.label.selling_price')"
                               prop="name" label-class-name="text-white" class-name="text-dark">
                <template slot-scope="scope">
                  <el-input @change="scope.row.id === 1 ? setSellingPrice($event) : (() => {})" size="small"
                            v-model.number="form.price_lists[scope.$index].value"></el-input>
                </template>
              </el-table-column>
            </el-table-column>
          </el-table>
          <!--    End price list section     -->
          <!--    Start department price section     -->
          <el-table size="small" stripe :data="form.departments" style="width: 100%"
                    header-cell-class-name="bg-secondary" class="mt-4">
            <el-table-column label-class-name="text-white text-center" :label="$t('item.label.store_prices')">
              <el-table-column :label="$t('warehouse.label.store')"
                               prop="name" label-class-name="text-white" class-name="text-dark">
                <template slot="header" slot-scope="scope">
                  {{ $t('warehouse.label.store') }} ({{ $t('warehouse.label.warehouse') }})
                </template>
                <template slot-scope="scope">{{ scope.row.name }} ({{ scope.row.wh }})</template>
              </el-table-column>
              <el-table-column :label="$t('item.label.selling_price')"
                               prop="name" label-class-name="text-white" class-name="text-dark">
                <template slot-scope="scope">
                  <el-input :disabled="!scope.row.wh" size="small"
                            v-model.number="form.departments[scope.$index].selling_price"></el-input>
                </template>
              </el-table-column>
            </el-table-column>
          </el-table>
          <!--    End department price section     -->
        </el-form>
      </el-col>
    </el-row>
    <div class="mb-5 pb-3"></div>
    <template #footer>
      <el-row class="dialog-footer">
        <el-col :span="12" :offset="6">
          <el-button @click="$emit('close', false)" type="danger" plain>{{ $t('button.cancel') }}</el-button>
          <el-button :disabled="!edited" :loading="loading" type="primary" @click="submitForm">
            {{ $t(`item.button.${edit ? 'update' : 'create'}`) }}
          </el-button>
        </el-col>
      </el-row>
    </template>
    <variant-dialog :display="variantDialog" @close="toggleVariantDialog" @created="newVariant"
                    @updated="updatedVariant" :item="item" :prev-variants="form.variants"></variant-dialog>
  </el-dialog>
</template>

<script>
import VariantDialog from '@/components/Items/Modal/VariantDialog.vue'

export default {
  name: 'CreateEditItem',
  components: { VariantDialog },
  props: {
    display: {
      type: Boolean,
      required: true
    },
    item: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data () {
    return {
      variantDialog: false,
      categories: [],
      suppliers: [],
      modifiers: [],
      taxes: [],
      itemModifiers: [],
      itemTaxes: [],
      editedItemVariant: {
        id: '',
        module: ''
      },
      snapshot: {},
      imageFile: null, // Store original File object for FormData upload
      originalVariants: new Map(), // Store original variant values for comparison
      savingVariants: {}, // Track which variants are currently being saved (object for reactivity)
      form: {
        name: '',
        short_name: '',
        description: '',
        unit_price: null,
        purchase_price: null,
        minimum_price: null,
        unit_type: null,
        item_type: 'product',
        visual_type: 'colour',
        sold_by: 'each',
        image: '',
        colour: '',
        barcode: '',
        sellable: true,
        tracked: false,
        reorder_level: null,
        optimal_order: null,
        category: null,
        supplier: null,
        price_lists: [],
        departments: [],
        modifiers: [],
        variants: [],
        taxes: []
      },
      itemVariants: [],
      predefineColours: [
        {
          value: '16729344',
          colour: '#25960b'
        },
        {
          value: '11156336',
          colour: '#aba298'
        },
        {
          value: '16766720',
          colour: '#ffd700'
        },
        {
          value: '9437184',
          colour: '#90ee90'
        },
        {
          value: '13520127',
          colour: '#00ced1'
        },
        {
          value: '1973791',
          colour: '#1e90ff'
        },
        {
          value: '1973791',
          colour: '#c71585'
        },
        {
          value: '16729345',
          colour: '#ff4500'
        },
        {
          value: '16750464',
          colour: '#ff7800'
        },
        {
          value: '19371839',
          colour: '#00babd'
        },
        {
          value: '1973791',
          colour: '#1f93ff'
        },
        {
          value: '13028405',
          colour: '#c71585'
        }
      ],
      rules: {
        name: [
          {
            required: true,
            message: this.$t('validation.required'),
            trigger: 'change'
          }
        ],
        item_type: [
          {
            required: true,
            message: this.$t('validation.required'),
            trigger: 'change'
          }
        ],
        category: [{
          required: true,
          message: this.$t('validation.required'),
          trigger: 'change'
        }],
        unit_price: [
          {
            required: false,
            message: this.$t('validation.required'),
            trigger: 'change'
          },
          {
            type: 'number',
            message: this.$t('validation.required'),
            trigger: 'change'
          }
        ],
        unit_type: [
          {
            required: false,
            message: this.$t('validation.required'),
            trigger: 'change'
          }
        ],
        reorder_level: [
          {
            required: false,
            message: this.$t('validation.required'),
            trigger: 'change'
          }
        ],
        optimal_order: [
          {
            required: false,
            message: this.$t('validation.required'),
            trigger: 'change'
          }
        ]
      },
      loading: false,
      draggedIndex: null,
      unitTypes: []
    }
  },
  computed: {
    submittable () {
      return (this.form.name.length && this.form.unit_type && this.form.unit_price)
    },
    edit () {
      return this.item && this.item.id
    },
    edited () {
      return JSON.stringify(this.form) !== JSON.stringify(this.snapshot)
    }
  },
  watch: {
    'form.visual_type' (value) {
      console.log(value)
      if (value === 'image') {
        if (this.edit && this.item.colour) {
          this.form.colour = this.item.colour
        } else {
          this.form.colour = ''
        }
      } else {
        this.form.colour = this.predefineColours[0].value
      }
    }
    /* 'form.unit_price' (value) {
      if (!this.edit) {
        this.form.price_lists[0].value = value
      }
    } */
  },
  methods: {
    setColour (colour) {
      if (this.form.colour === colour.colour) {
        this.form.colour = ''
      } else {
        this.form.colour = colour.colour
      }
    },
    updateEditedItemVariant (itemVariantId, itemVariantModule) {
      this.editedItemVariant.id = itemVariantId
      this.editedItemVariant.module = itemVariantModule
    },
    /**
     * Handle variant field change - marks variant as modified and exits edit mode
     * @param {Object} variant - The variant row object
     * @param {String} field - The field name that was changed
     */
    handleVariantFieldChange (variant, field) {
      // Exit edit mode
      this.editedItemVariant = { id: '', module: '' }

      // Mark variant as modified if value differs from original
      if (this.originalVariants.has(variant.id)) {
        const original = this.originalVariants.get(variant.id)
        const hasChanged = original[field] !== variant[field]

        if (hasChanged) {
          // Variant is modified - this will trigger the check icon to appear
          // The isVariantModified method will detect this change
        }
      }
    },
    /**
     * Check if a variant has been modified from its original values
     * @param {Number} variantId - The variant ID to check
     * @returns {Boolean} - True if variant has unsaved changes
     */
    isVariantModified (variantId) {
      if (!this.originalVariants.has(variantId)) {
        return false
      }

      const variant = this.item.variants.find(v => v.id === variantId)
      if (!variant) {
        return false
      }

      const original = this.originalVariants.get(variantId)

      // Normalize numeric values - handle null/undefined as null, convert to number
      const normalizeNumber = (val) => {
        if (val === null || val === undefined || val === '') return null
        const num = Number(val)
        return isNaN(num) ? null : num
      }

      // Normalize string values - handle null/undefined as empty string
      const normalizeString = (val) => {
        if (val === null || val === undefined) return ''
        return String(val).trim()
      }

      // Compare unit_price
      const originalUnitPrice = normalizeNumber(original.unit_price)
      const variantUnitPrice = normalizeNumber(variant.unit_price)
      const unitPriceChanged = originalUnitPrice !== variantUnitPrice

      // Compare purchase_price
      const originalPurchasePrice = normalizeNumber(original.purchase_price)
      const variantPurchasePrice = normalizeNumber(variant.purchase_price)
      const purchasePriceChanged = originalPurchasePrice !== variantPurchasePrice

      // Compare barcode
      const originalBarcode = normalizeString(original.barcode)
      const variantBarcode = normalizeString(variant.barcode)
      const barcodeChanged = originalBarcode !== variantBarcode

      return unitPriceChanged || purchasePriceChanged || barcodeChanged
    },
    /**
     * Check if a variant is currently being saved
     * @param {Number} variantId - The variant ID to check
     * @returns {Boolean} - True if variant is being saved
     */
    isVariantSaving (variantId) {
      return !!this.savingVariants[variantId]
    },
    /**
     * Save a single variant's changes via HTTP
     * @param {Object} variant - The variant object to save
     */
    async saveVariant (variant) {
      if (!variant || !variant.id) {
        console.error('Invalid variant data for save')
        return
      }

      // Check if variant is actually modified
      if (!this.isVariantModified(variant.id)) {
        return
      }

      // Prevent duplicate saves
      if (this.savingVariants[variant.id]) {
        return
      }

      // Use Vue.set for reactivity
      this.$set(this.savingVariants, variant.id, true)

      try {
        // Prepare update data - only send changed fields
        const original = this.originalVariants.get(variant.id)
        const updateData = {}

        if (original.unit_price !== variant.unit_price) {
          updateData.unit_price = variant.unit_price
        }
        if (original.purchase_price !== variant.purchase_price) {
          updateData.purchase_price = variant.purchase_price
        }
        if (original.barcode !== variant.barcode) {
          updateData.barcode = variant.barcode
        }

        // Update variant via HTTP
        const updatedVariant = await this.$itemService.updateItemVariant(variant.id, updateData)

        // Normalize the response values using same logic as comparison
        const normalizeNumber = (val) => {
          if (val === null || val === undefined || val === '') return null
          const num = Number(val)
          return isNaN(num) ? null : num
        }
        const normalizeString = (val) => {
          if (val === null || val === undefined) return ''
          return String(val).trim()
        }

        const normalizedResponse = {
          unit_price: normalizeNumber(updatedVariant.unit_price),
          purchase_price: normalizeNumber(updatedVariant.purchase_price),
          barcode: normalizeString(updatedVariant.barcode)
        }
        console.log(normalizedResponse)

        // Update the original snapshot with new values (normalized)
        // Create a new Map to ensure Vue reactivity
        const newOriginalVariants = new Map(this.originalVariants)
        newOriginalVariants.set(variant.id, { ...normalizedResponse })
        this.originalVariants = newOriginalVariants

        // Update the variant in the item.variants array using Vue.set for reactivity
        const variantIndex = this.item.variants.findIndex(v => v.id === variant.id)
        if (variantIndex > -1) {
          // Use Vue.set to ensure reactivity when updating nested prop
          this.$set(this.item.variants[variantIndex], 'unit_price', normalizedResponse.unit_price)
          this.$set(this.item.variants[variantIndex], 'purchase_price', normalizedResponse.purchase_price)
          this.$set(this.item.variants[variantIndex], 'barcode', normalizedResponse.barcode)
        }

        // Wait for Vue to process the updates
        await this.$nextTick()

        // Get the fresh variant from the array after update (not the old reference)
        const updatedVariantFromArray = this.item.variants.find(v => v.id === variant.id)

        // Double-check that the variant is no longer modified using the fresh reference
        if (updatedVariantFromArray) {
          const stillModified = this.isVariantModified(variant.id)
          if (stillModified) {
            console.warn('Variant still appears modified after save.', {
              variantId: variant.id,
              original: this.originalVariants.get(variant.id),
              current: {
                unit_price: updatedVariantFromArray.unit_price,
                purchase_price: updatedVariantFromArray.purchase_price,
                barcode: updatedVariantFromArray.barcode
              },
              normalizedResponse
            })
          }
        }

        this.notify(
          this.$t('variant.message.updated') || 'Variant updated successfully',
          this.$t('status.success') || 'Success'
        )
      } catch (error) {
        console.error('Error updating variant:', error)
        this.notify(
          error.response?.data?.message || this.$t('variant.message.update_failed') || 'Failed to update variant',
          this.$t('status.error') || 'Error',
          'error'
        )

        // Revert to original values on error
        if (this.originalVariants.has(variant.id)) {
          const original = this.originalVariants.get(variant.id)
          const variantIndex = this.item.variants.findIndex(v => v.id === variant.id)
          if (variantIndex > -1) {
            // Use Vue.set for reactivity when reverting
            this.$set(this.item.variants[variantIndex], 'unit_price', original.unit_price)
            this.$set(this.item.variants[variantIndex], 'purchase_price', original.purchase_price)
            this.$set(this.item.variants[variantIndex], 'barcode', original.barcode)
          }
        }
      } finally {
        // Use Vue.delete for reactivity
        this.$delete(this.savingVariants, variant.id)
      }
    },
    updateItemVariants (data = null) {
      if (data !== null) {
        this.itemVariants = data
      } else {
        this.$itemService.getItemVariants(this.item.id)
          .then(data => {
            this.itemVariants = data
          })
      }
    },
    deleteAllVariants () {
      this.askQuestion(this.$t('variant.confirm.delete_all'), async () => {
        this.loading = true
        this.$itemService.deleteVariant(this.item.id)
          .then(data => {
            // Clear variant tracking when all variants are deleted
            this.originalVariants.clear()
            this.savingVariants = {}
            this.$emit('updated', data)
            this.loading = false
            this.notify(this.$t('variant.message.deleted'), this.$t('status.success'))
          })
      }, this.$t('variant.title.delete_all'), () => {
      }, 'error', { confirmButtonClass: 'el-button--danger' })
    },
    itemVariantDelete (index, id) {
      this.askQuestion(this.$t('variant.confirm.delete'), async () => {
        this.loading = true
        this.$itemService.deleteItemVariant(id)
          .then(data => {
            // Clean up tracking for deleted variant
            this.originalVariants.delete(id)
            this.$delete(this.savingVariants, id)
            this.itemVariants.splice(index, 1)
            this.loading = false
            this.notify(this.$t('variant.message.deleted'), this.$t('status.success'))
          })
      })
    },
    setSellingPrice (value) {
      this.form.unit_price = parseInt(value)
    },
    handleImageRemove () {
      this.form.image = ''
      this.imageFile = null
    },
    encodeImageFileAsURL (file, loc) {
      // Store the original File object for FormData upload
      if (file instanceof File) {
        this.imageFile = file
      }
      const reader = new FileReader()
      reader.onloadend = () => {
        this.form.image = reader.result
        return false
      }
      reader.onerror = () => {
        console.error('Error reading image file')
        this.imageFile = null
      }
      reader.readAsDataURL(file)
    },
    beforeImageUpload (image) {
      // Prevent default upload, we handle it manually
      this.encodeImageFileAsURL(image)
      return false // Prevent default upload behavior
    },
    handleImageSuccess () {
    },
    fetchTypeSuggestions (queryString, cb) {
      cb(queryString
        ? (this.unitTypes.filter(t => {
            return t.value && t.value.toLowerCase().indexOf(queryString.toLowerCase()) === 0
          }))
        : this.unitTypes)
    },
    open () {
      this.fetchData()
    },
    opened () {
      if (this.edit) {
        /* Object.keys(this.form).forEach((k) => {
          if (k === 'category') {
            this.form[k] = this.item.category_id
          } else if (k === 'supplier') {
            this.form[k] = this.item.supplier_id
          } else {
            this.form[k] = this.item[k]
          }
        }) */
        this.form.name = this.item.name
        this.form.short_name = this.item.short_name || ''
        this.form.description = this.item.description
        this.form.unit_price = this.item.unit_price
        this.form.purchase_price = this.item.purchase_price
        this.form.minimum_price = this.item.minimum_price
        this.form.unit_type = this.item.unit_type
        this.form.item_type = this.item.item_type
        this.form.visual_type = this.item.visual_type === 'colour' ? 'colour' : 'image'
        this.form.sold_by = this.item.sold_by !== undefined ? this.item.sold_by : 'each'
        this.form.image = this.item.image
        this.form.colour = this.item.colour
        this.form.barcode = this.item.barcode
        this.form.sellable = this.item.sellable !== undefined ? this.item.sellable : true
        this.form.reorder_level = this.item.reorder_level
        this.form.optimal_order = this.item.optimal_order
        this.form.category = this.item.category_id
        this.form.supplier = this.item.supplier_id
        this.form.price_lists = this.item.price_lists
        this.form.departments = this.item.departments
        this.form.modifiers = this.item.modifiers
        this.form.variants = [] // this.item.variants
        this.form.taxes = this.item.taxes

        // Store original variant values for change tracking
        this.storeOriginalVariants()
      } else {
        this.colour = this.predefineColours[0].value
      }
      this.snapshot = Object.assign({}, this.form)
      // Ref-safety check: ensure inputName exists before accessing
      this.$nextTick(() => {
        if (this.$refs.inputName && this.$refs.inputName.$el) {
          const inputElement = this.$refs.inputName.$el.querySelector('input') || this.$refs.inputName.$el
          if (inputElement && typeof inputElement.focus === 'function') {
            inputElement.focus()
          }
        }
      })
    },
    clearFields () {
      // Ref-safety check: ensure itemForm exists before accessing
      if (this.$refs.itemForm && typeof this.$refs.itemForm.resetFields === 'function') {
        this.$refs.itemForm.resetFields()
      }
    },
    closed () {
      this.clearFields()
      this.form.name = ''
      this.form.short_name = ''
      this.form.description = ''
      this.form.unit_price = null
      this.form.purchase_price = null
      this.form.minimum_price = null
      this.form.unit_type = null
      this.form.item_type = 'product'
      this.form.sellable = true
      this.form.sold_by = 'each'
      this.form.visual_type = 'colour'
      this.form.reorder_level = null
      this.form.category = null
      this.form.barcode = ''
      this.form.image = ''
      this.form.supplier = null
      this.form.modifiers = []
      this.form.variants = []
      this.form.taxes = []
      this.imageFile = null // Reset image file
      this.loading = false
      // Reset variant tracking
      this.originalVariants.clear()
      this.savingVariants = {}
    },
    /**
     * Store original variant values for change tracking
     */
    storeOriginalVariants () {
      this.originalVariants.clear()
      if (this.item.variants && Array.isArray(this.item.variants)) {
        this.item.variants.forEach(variant => {
          if (variant.id) {
            // Normalize values when storing to ensure consistent comparisons
            // Use same normalization logic as isVariantModified
            const normalizeNumber = (val) => {
              if (val === null || val === undefined || val === '') return null
              const num = Number(val)
              return isNaN(num) ? null : num
            }
            const normalizeString = (val) => {
              if (val === null || val === undefined) return ''
              return String(val).trim()
            }
            this.originalVariants.set(variant.id, {
              unit_price: normalizeNumber(variant.unit_price),
              purchase_price: normalizeNumber(variant.purchase_price),
              barcode: normalizeString(variant.barcode)
            })
          }
        })
      }
    },
    toggleVariantDialog (value) {
      if (value === undefined) value = false
      this.variantDialog = value
    },
    newVariant (data) {
      if (data && data.length) {
        this.form.variants = data
      } else {
        this.form.variants = []
      }
      // Refresh original variants tracking after new variants are added
      this.$nextTick(() => {
        this.storeOriginalVariants()
      })
    },
    updatedVariant (data) {
      if (data && data.length) {
        this.form.variants = data
      } else {
        this.form.variants = []
      }
      // Refresh original variants tracking after variant dialog updates
      this.$nextTick(() => {
        this.storeOriginalVariants()
      })
    },
    updateItemModifier (value, modifier) {
      if (value) {
        this.$nextTick(_ => (this.form.modifiers.push(modifier)))
      } else {
        this.$nextTick(_ => {
          const index = this.form.modifiers.indexOf(this.form.modifiers.find(m => m.id === modifier.id))
          this.form.modifiers.splice(index, 1)
        })
      }
    },
    async updateItemTax (value, tax) {
      if (value) {
        this.$nextTick(_ => (this.form.taxes.push(tax)))
      } else {
        this.$nextTick(_ => {
          const index = this.form.taxes.indexOf(this.form.taxes.find(m => m.id === tax.id))
          this.form.taxes.splice(index, 1)
        })
      }
      /* const tax = this.taxes.find(m => m.id === data.id)
      tax.selected = value
      if (value) {
        const itemTax = {
          item_id: this.item.id,
          tax_id: data.id
        }
        await this.$itemService.createItemTax(itemTax)
          .then(res => {
          })
      } else {
        const itemTax = this.itemTaxes.find(im => im.tax_id === data.id && im.item_id === this.item.id)
        await this.$itemService.deleteItemTax(itemTax.id)
          .then(res => {
          })
      } */
    },
    async submitForm () {
      // Ref-safety check: ensure itemForm exists before accessing
      if (!this.$refs.itemForm) {
        console.error('Form ref not available')
        this.notify('Form validation failed', 'Error', 'error')
        return
      }

      this.$refs.itemForm.validate(async (valid) => {
        if (!valid) {
          console.error('Form validation failed')
          this.notify(this.$t('validation.form_invalid') || 'Please correct the form errors', 'Error', 'error')
          return
        }

        try {
          await this.withLoading(async () => {
            this.askQuestion(this.$t(`item.confirm.${this.edit ? 'update' : 'create'}`), async () => {
              this.loading = true
              try {
                // Prepare submission data - use FormData if image file exists, otherwise use JSON
                let submissionData
                const hasNewImageFile = this.imageFile && this.imageFile instanceof File

                console.log('Submitting item:', {
                  edit: this.edit,
                  hasNewImageFile,
                  itemId: this.edit ? this.item.id : null
                })

                // If we have a new image file, create FormData and append image
                if (hasNewImageFile) {
                  const formData = new FormData()

                  // Append all form fields to FormData
                  Object.keys(this.form).forEach(key => {
                    const value = this.form[key]
                    // Skip image field if it's a base64 string - we'll use the File instead
                    if (key === 'image') {
                      formData.append('image', this.imageFile)
                    } else if (value !== null && value !== undefined) {
                      // Handle arrays and objects by stringifying them
                      if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
                        formData.append(key, JSON.stringify(value))
                      } else {
                        formData.append(key, value)
                      }
                    }
                  })

                  submissionData = formData
                  console.log('Using FormData for submission (new image file present)')
                } else {
                  // Use JSON format - send form data as-is
                  // When editing, form.image will be the existing image URL
                  // When creating, form.image will be base64 string if image was selected
                  submissionData = this.form

                  console.log('Using JSON for submission (no new image file)')
                }

                let data
                if (this.edit) {
                  console.log('Updating item:', this.item.id)
                  data = await this.$itemService.updateItem(this.item.id, submissionData, hasNewImageFile)
                  console.log('Update successful:', data)
                  this.showSuccess(this.$t('item.message.updated'))
                  this.$emit('updated', data)
                } else {
                  console.log('Creating new item')
                  data = await this.$itemService.createItem(submissionData, hasNewImageFile)
                  console.log('Create successful:', data)
                  this.showSuccess(this.$t('item.message.created'))
                  this.$emit('created', data)
                }
                this.$emit('close', false)
                // Ref-safety check before resetFields
                if (this.$refs.itemForm && typeof this.$refs.itemForm.resetFields === 'function') {
                  this.$refs.itemForm.resetFields()
                }
              } catch (error) {
                // Error is already handled by the global interceptor
                console.error('Error in saveItem:', error)
                console.error('Error details:', {
                  message: error.message,
                  response: error.response?.data,
                  status: error.response?.status
                })
                // Don't re-throw - global interceptor should handle it, but ensure we reset loading state
              } finally {
                this.loading = false
              }
            }, null, () => {
              // Cancel callback - user cancelled the confirmation
              console.log('User cancelled item submission')
              this.loading = false
            })
          }, 'loading')
        } catch (error) {
          // Error is already handled by the global interceptor
          console.error('Component caught error in saveItem:', error)
          this.loading = false
        }
      })
    },
    fetchSuppliers () {
      return new Promise((resolve, reject) => {
        this.$supplierService.getSuppliers()
          .then(resolve)
          .catch(reject)
      })
    },
    async fetchData () {
      this.$categoryService.getCategories()
        .then(data => {
          this.categories = data
        })
      this.suppliers = await this.fetchSuppliers()
      setTimeout(() => {
        this.$configService.getPriceLists()
          .then(data => {
            this.form.price_lists = data.map(pl => {
              let value = null
              if (this.edit) {
                const ipl = this.item.price_lists.find(ipl => ipl.id === pl.id)
                if (ipl && ipl.id) {
                  value = ipl.pivot.price
                }
              }
              return {
                id: pl.id,
                name: pl.name,
                value
              }
            })
          })
        if (this.edit) {
          this.$departmentService.getDepartmentsWithWarehouseItem(this.item.id)
            .then(data => {
              this.form.departments = data.map(d => {
                return {
                  id: d.id,
                  wh: d.warehouse ? d.warehouse.name : null,
                  name: d.name,
                  selling_price: d.warehouse && d.warehouse.items.length ? d.warehouse.items[0].pivot.selling_price : null
                }
              })
            })
        } else {
          this.$departmentService.getDepartmentsWithWarehouseItem()
            .then(data => (this.form.departments = data.map(d => ({
              id: d.id,
              wh: d.warehouse.name,
              name: d.name,
              selling_price: null
            }))))
        }
      }, 200)
      this.$itemService.getItemsUnitTypes()
        .then(data => (this.unitTypes = data))
      /* await this.$itemService.getItemModifiers()
        .then(data => {
          this.itemModifiers = data
        }) */
      await this.$modifierService.getModifiers()
        .then(data => {
          this.modifiers = data.map(m => ({
            ...m,
            selected: this.item.modifiers ? !!this.item.modifiers.find(mm => m.id === mm.id) : false
          }))
        })
      // await this.$itemService.getItemTaxes()
      //   .then(data => {
      //     this.itemTaxes = data
      //   })
      await this.$taxService.getTaxes()
        .then(data => {
          this.taxes = data.map(t => ({
            ...t,
            selected: this.item.taxes ? !!this.item.taxes.find(tt => t.id === tt.id) : false
          }))
          /* for (const tax of data) {
            if (this.item && this.item.id) {
              const record = this.itemTaxes.find(im => im.tax_id === tax.id && im.item_id === this.item.id)
              tax.selected = !!record
            } else {
              tax.selected = false
            }
          }
          this.taxes = data */
        })
      await this.updateItemVariants()

      // Store original variant values after variants are loaded
      if (this.edit && this.item.variants) {
        this.$nextTick(() => {
          this.storeOriginalVariants()
        })
      }
    }
  },
  mounted () {
  }
}
</script>

<style>
.el-dialog__footer {
  bottom: 0;
  left: 0;
  right: 0;
  position: fixed;
  background-color: white;
  padding: 10px;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1);
  z-index: 99999999999999999;
}

input[type="file"].el-upload__input {
  display: none;
}

.avatar-uploader .el-upload {
  position: inherit !important;
}

.avatar-uploader .el-upload {
  position: fixed;
  overflow: hidden;
  width: 100px;
  height: 100px;
  display: block;
}

.avatar-uploader-icon:hover {
  opacity: .4;
  background-color: rgba(21, 21, 21, .5);
  color: #FFFFFF;
}

.avatar-uploader-icon {
  border: 2px dashed #d9d9d9;
  border-radius: 6px;
  font-size: 28px;
  color: #8c939d;
  width: 100px;
  height: 100px;
  line-height: 90px;
  text-align: center;
  cursor: pointer;
}

.avatar-uploader-icon:hover {
  opacity: 1;
}

[class^="el-icon-"] {
  display: inherit;
}

.custom-avatar {
  width: 100px;
  height: 100px;
  display: block;
  border: 2px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
}

.el-tag + .el-tag {
  margin-left: 10px;
}

.button-new-tag {
  margin-left: 10px;
  height: 32px;
  line-height: 30px;
  padding-top: 0;
  padding-bottom: 0;
}

.input-new-tag {
  width: 90px;
  margin-left: 10px;
  vertical-align: bottom;
}
</style>
